import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import { Observable } from 'rxjs/Observable';
import { TranslateService } from '@ngx-translate/core';
import { FullUser } from '../../models/full-user.model';
import {
  EmployeeClothingOverview,
  EmployeeClothingEntitlementsSource,
  ClothingProfileStorekeeper,
  ClothingLossCasePayload,
  ClothingRequestPayload,
  StorekeeperIssuePayload,
  StorekeeperRejectNoStockPayload
} from '../../models/clothing.model';
import { ProfileClothingViewItem } from '../../models/profile-clothing.model';
import { User } from '../../models/user.model';
import { ClothingService } from '../../services/clothing.service';
import { ClothingErrorService } from '../../services/clothing-error.service';
import { ProfileClothingViewService } from '../../services/profile-clothing-view.service';
import { ToastService } from '../../services/toast.service';
import { UserService } from '../../services/user.service';
import { ClothingIssueModalComponent } from '../../modals/clothing-issue-modal.component';
import { ClothingLossModalComponent } from '../../modals/clothing-loss-modal.component';

@Component({
  selector: 'app-my-profile',
  templateUrl: './my-profile.component.html',
  styleUrls: ['./my-profile.component.css']
})
export class MyProfileComponent implements OnInit {
  private user: User;
  public userAPI: FullUser;
  public isLoaded = false;
  public errorInfo = false;
  public isAdmin = false;
  public isAdmins = false;
  public isUser = false;
  public isHead = false;
  public isStorekeeper = false;
  public showEmployeeClothing = false;
  public showStorekeeperClothing = false;
  public showClothingPanel = false;
  public isClothingLoaded = false;
  public clothingError = false;
  public clothingItems: ProfileClothingViewItem[] = [];
  public clothingStorekeeper: ClothingProfileStorekeeper;

  private requestCatalogItemId = '';
  private lossCatalogItemId = '';
  private issueActionKey = '';
  private rejectRequestId = '';

  constructor(
    private userService: UserService,
    private clothingService: ClothingService,
    private clothingErrorService: ClothingErrorService,
    private profileClothingViewService: ProfileClothingViewService,
    private toastService: ToastService,
    private translate: TranslateService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.user = this.userService.getLocal();
    this.setRoleFlags();
    this.loadProfile();
    this.loadClothing();
  }

  trackByClothingItem(index: number, item: ProfileClothingViewItem): string {
    return item.catalogItemId;
  }

  trackByRole(index: number, role: string): string {
    return role;
  }

  showMobileProfile(): boolean {
    return !!this.userAPI && !this.errorInfo;
  }

  getMobileProfileInitials(): string {
    if (!this.userAPI) {
      return '';
    }
    var lastInitial = this.userAPI.lastName ? this.userAPI.lastName.charAt(0) : '';
    var firstInitial = this.userAPI.firstName ? this.userAPI.firstName.charAt(0) : '';
    var initials = (lastInitial + firstInitial).toUpperCase();
    return initials || '?';
  }

  getMobileScheduleTime(): string {
    if (!this.userAPI || !this.userAPI.schedule || !this.userAPI.schedule.schedule) {
      return '';
    }

    var schedule = this.userAPI.schedule.schedule;
    var workStart = schedule.workStart || '';
    var workEnd = schedule.workEnd || '';

    if (!workStart && !workEnd) {
      return '';
    }

    if (!workStart) {
      return workEnd;
    }

    if (!workEnd) {
      return workStart;
    }

    return workStart + ' - ' + workEnd;
  }

  isRequestPending(item: ProfileClothingViewItem): boolean {
    return this.requestCatalogItemId === item.catalogItemId;
  }

  isLossPending(item: ProfileClothingViewItem): boolean {
    return this.lossCatalogItemId === item.catalogItemId;
  }

  isIssuePending(item: ProfileClothingViewItem): boolean {
    return this.issueActionKey === this.createIssueActionKey(item);
  }

  isRejectPending(item: ProfileClothingViewItem): boolean {
    return !!item.requestId && this.rejectRequestId === item.requestId;
  }

  getClothingHintKey(): string {
    if (this.showStorekeeperClothing) {
      return 'CLOTHING_STOREKEEPER_PROFILE_HINT';
    }
    return 'CLOTHING_EMPLOYEE_PROFILE_HINT';
  }

  showStorekeeperContact(): boolean {
    return !!this.clothingStorekeeper && !!this.clothingStorekeeper.userId;
  }

  hasStorekeeperActions(item: ProfileClothingViewItem): boolean {
    return this.showStorekeeperClothing;
  }

  showIssueAction(item: ProfileClothingViewItem): boolean {
    return this.showStorekeeperClothing;
  }

  showSelfStorekeeperIssueAction(): boolean {
    return this.showEmployeeClothing && this.isStorekeeper;
  }

  showEmployeeRequestAction(): boolean {
    if (!this.showEmployeeClothing) {
      return false;
    }
    return !this.isStorekeeper;
  }

  isSelfStorekeeperIssueDisabled(item: ProfileClothingViewItem): boolean {
    if (item.requestDisabled) {
      return true;
    }
    return item.issueDisabled || this.isIssuePending(item);
  }

  createEmployeeRequest(item: ProfileClothingViewItem): void {
    if (item.requestDisabled || this.isRequestPending(item)) {
      return;
    }
    this.requestCatalogItemId = item.catalogItemId;
    this.clothingService.createEmployeeRequest(this.createRequestPayload(item)).subscribe(
      () => this.handleRequestSuccess(),
      (error) => this.handleRequestError(error)
    );
  }

  createEmployeeLossCase(item: ProfileClothingViewItem): void {
    if (item.lossDisabled || this.isLossPending(item)) {
      return;
    }
    if (this.shouldCreateRequestForHeadLoss()) {
      this.createHeadLossRequest(item);
      return;
    }
    this.openLossModal(item);
  }

  issueStorekeeperItem(item: ProfileClothingViewItem): void {
    if (this.showSelfStorekeeperIssueAction() && this.isSelfStorekeeperIssueDisabled(item)) {
      return;
    }
    if (item.issueDisabled || this.isIssuePending(item)) {
      return;
    }
    this.openIssueModal(item);
  }

  rejectStorekeeperRequest(item: ProfileClothingViewItem): void {
    if (item.rejectDisabled || this.isRejectPending(item)) {
      return;
    }
    this.rejectRequestId = item.requestId;
    this.clothingService.rejectStorekeeperRequest(
      item.requestId,
      this.createRejectPayload()
    ).subscribe(
      () => this.handleRejectSuccess(),
      (error) => this.handleRejectError(error)
    );
  }

  getLifeWidth(item: ProfileClothingViewItem): string {
    return item.lifePercent + '%';
  }

  private setRoleFlags(): void {
    this.isAdmins = this.hasRole('ROLE_ADMIN');
    this.isHead = this.hasRole('ROLE_HEAD');
    this.isUser = this.hasRole('ROLE_USER');
    this.isStorekeeper = this.hasStorekeeperRole();
    this.showStorekeeperClothing = false;
    this.showEmployeeClothing = this.canShowOwnEmployeeClothing();
    this.showClothingPanel = this.showEmployeeClothing || this.showStorekeeperClothing;
  }

  private hasRole(role: string): boolean {
    if (!this.user || !this.user.roles) {
      return false;
    }
    return this.user.roles.indexOf(role) !== -1;
  }

  private hasStorekeeperRole(): boolean {
    return this.hasRole('ROLE_STOREKEEPER') || this.hasRole('STOREKEEPER');
  }

  private canShowOwnEmployeeClothing(): boolean {
    return this.isUser || this.isHead || this.isAdmins || this.isStorekeeper;
  }

  private shouldCreateRequestForHeadLoss(): boolean {
    return this.isHead && !this.isStorekeeper;
  }

  private loadProfile(): void {
    this.userService.getUserFromAPI(this.user.id).subscribe(
      (response) => this.handleProfileResponse(response.body as FullUser),
      () => this.handleProfileError()
    );
  }

  private handleProfileResponse(data: FullUser): void {
    this.userAPI = data;
    this.isAdmin = this.hasApiRole('ADMIN', data);
    this.isLoaded = true;
    this.errorInfo = false;
  }

  private hasApiRole(role: string, profile: FullUser): boolean {
    if (!profile || !profile.roles) {
      return false;
    }
    return profile.roles.indexOf(role) !== -1;
  }

  private handleProfileError(): void {
    this.isLoaded = true;
    this.errorInfo = true;
  }

  private loadClothing(): void {
    if (!this.showClothingPanel) {
      return;
    }
    if (this.showStorekeeperClothing) {
      this.loadStorekeeperClothing();
      return;
    }
    this.loadEmployeeClothing();
  }

  private loadEmployeeClothing(): void {
    this.isClothingLoaded = false;
    this.clothingError = false;
    Observable.forkJoin(
      this.loadEmployeeOverviewSafely(),
      this.clothingService.getEmployeeEntitlements()
    ).subscribe(
      (response) => this.handleEmployeeClothingSuccess(response),
      () => this.handleClothingError()
    );
  }

  private loadEmployeeOverviewSafely(): Observable<EmployeeClothingOverview> {
    return this.clothingService.getEmployeeOverview()
      .catch(() => Observable.of(null));
  }

  private loadStorekeeperClothing(): void {
    this.isClothingLoaded = false;
    this.clothingError = false;
    Observable.forkJoin(
      this.clothingService.getStorekeeperEmployeeOverview(this.user.id),
      this.clothingService.getStorekeeperEmployeeEntitlements(this.user.id)
    ).subscribe(
      (response) => this.handleStorekeeperClothingSuccess(response),
      () => this.handleClothingError()
    );
  }

  private handleEmployeeClothingSuccess(response: Object[]): void {
    var overview = response[0] as EmployeeClothingOverview;
    var items = this.profileClothingViewService.mapEmployeeOverview(
      overview,
      response[1] as EmployeeClothingEntitlementsSource
    );
    this.handleClothingSuccess(overview, items);
  }

  private handleStorekeeperClothingSuccess(response: Object[]): void {
    var overview = response[0] as EmployeeClothingOverview;
    var items = this.profileClothingViewService.mapSelfStorekeeperOverview(
      overview,
      response[1] as EmployeeClothingEntitlementsSource
    );
    this.handleClothingSuccess(overview, items);
  }

  private handleClothingSuccess(
    overview: EmployeeClothingOverview,
    items: ProfileClothingViewItem[]
  ): void {
    this.clothingItems = items;
    this.clothingStorekeeper = this.resolveClothingStorekeeper(overview);
    this.isClothingLoaded = true;
    this.clothingError = false;
    this.showClothingPanel = this.hasClothingItems(items);
    this.clearPendingActions();
  }

  private handleClothingError(): void {
    this.clothingItems = [];
    this.clothingStorekeeper = null;
    this.isClothingLoaded = true;
    this.clothingError = true;
    this.showClothingPanel = this.showEmployeeClothing || this.showStorekeeperClothing;
    this.clearPendingActions();
  }

  private hasClothingItems(items: ProfileClothingViewItem[]): boolean {
    return !!items && !!items.length;
  }

  private resolveClothingStorekeeper(
    overview: EmployeeClothingOverview
  ): ClothingProfileStorekeeper {
    if (!overview || !overview.storekeeper || !overview.storekeeper.userId) {
      return null;
    }
    return overview.storekeeper;
  }

  private createRequestPayload(item: ProfileClothingViewItem): ClothingRequestPayload {
    return {
      catalogItemId: item.catalogItemId,
      requestedQuantity: 1
    };
  }

  private createLossPayload(
    item: ProfileClothingViewItem,
    description: string
  ): ClothingLossCasePayload {
    return {
      catalogItemId: item.catalogItemId,
      description: description
    };
  }

  private createRejectPayload(): StorekeeperRejectNoStockPayload {
    return {
      description: ''
    };
  }

  private createHeadLossRequest(item: ProfileClothingViewItem): void {
    this.lossCatalogItemId = item.catalogItemId;
    this.clothingService.createEmployeeRequest(this.createRequestPayload(item)).subscribe(
      () => this.handleHeadLossRequestSuccess(),
      (error) => this.handleHeadLossRequestError(error)
    );
  }

  private handleRequestSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_REQUEST_CREATED'));
    this.loadClothing();
  }

  private handleLossSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_LOSS_CREATED'));
    this.loadClothing();
  }

  private handleHeadLossRequestSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_REQUEST_CREATED'));
    this.loadClothing();
  }

  private handleIssueSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_ISSUE_SUCCESS'));
    this.loadClothing();
  }

  private handleRejectSuccess(): void {
    this.toastService.success(this.translate.instant('CLOTHING_REJECT_SUCCESS'));
    this.loadClothing();
  }

  private handleRequestError(error: HttpErrorResponse): void {
    this.requestCatalogItemId = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_REQUEST_CREATE_ERROR'));
  }

  private handleLossError(error: HttpErrorResponse): void {
    this.lossCatalogItemId = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_LOSS_CREATE_ERROR'));
  }

  private handleHeadLossRequestError(error: HttpErrorResponse): void {
    this.lossCatalogItemId = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_REQUEST_CREATE_ERROR'));
  }

  private handleIssueError(error: HttpErrorResponse): void {
    this.issueActionKey = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_ISSUE_ERROR'));
  }

  private handleRejectError(error: HttpErrorResponse): void {
    this.rejectRequestId = '';
    this.toastService.error(this.resolveErrorMessage(error, 'CLOTHING_REJECT_ERROR'));
  }

  private resolveErrorMessage(error: HttpErrorResponse, fallbackKey: string): string {
    return this.clothingErrorService.resolveMessage(error, fallbackKey);
  }

  private clearPendingActions(): void {
    this.requestCatalogItemId = '';
    this.lossCatalogItemId = '';
    this.issueActionKey = '';
    this.rejectRequestId = '';
  }

  private issueStorekeeperItemByMode(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    if (item.issueByRequest) {
      this.issueStorekeeperRequest(item, payload);
      return;
    }
    this.directIssueStorekeeperItem(item, payload);
  }

  private issueStorekeeperRequest(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    this.clothingService.issueStorekeeperRequest(
      item.requestId,
      payload
    ).subscribe(
      () => this.handleIssueSuccess(),
      (error) => this.handleIssueError(error)
    );
  }

  private directIssueStorekeeperItem(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    this.clothingService.directIssueStorekeeperItem(
      this.user.id,
      item.catalogItemId,
      payload
    ).subscribe(
      () => this.handleIssueSuccess(),
      (error) => this.handleIssueError(error)
    );
  }

  private createIssueActionKey(item: ProfileClothingViewItem): string {
    if (item.requestId) {
      return item.requestId;
    }
    return item.catalogItemId;
  }

  private openIssueModal(item: ProfileClothingViewItem): void {
    var modalRef = this.modalService.open(ClothingIssueModalComponent);
    modalRef.componentInstance.setValues(item.name, item.issueQuantity);
    modalRef.result.then(
      (payload) => this.submitStorekeeperIssue(item, payload),
      () => null
    );
  }

  private openLossModal(item: ProfileClothingViewItem): void {
    var modalRef = this.modalService.open(ClothingLossModalComponent);

    modalRef.componentInstance.setValues(item.name, '');
    modalRef.result.then(
      (description) => this.submitEmployeeLossCase(item, description),
      () => null
    );
  }

  private submitEmployeeLossCase(
    item: ProfileClothingViewItem,
    description: string
  ): void {
    if (!description) {
      return;
    }
    this.lossCatalogItemId = item.catalogItemId;
    this.clothingService.createEmployeeLossCase(
      this.createLossPayload(item, description)
    ).subscribe(
      () => this.handleLossSuccess(),
      (error) => this.handleLossError(error)
    );
  }

  private submitStorekeeperIssue(
    item: ProfileClothingViewItem,
    payload: StorekeeperIssuePayload
  ): void {
    if (!payload) {
      return;
    }
    this.issueActionKey = this.createIssueActionKey(item);
    this.issueStorekeeperItemByMode(item, payload);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-profile/my-profile.component.ts